import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import type { LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";
import "./loom-product-tree.css";

interface LoomProductTreeProps {
  readonly aircraftName?: string;
  readonly document: LoomAuthoringDocument;
  readonly onOpenSection?: (nodeId: string, sectionId: string) => void;
  readonly onSelect: (nodeId: string) => void;
  readonly onSelectControl?: (nodeId: string, controlSurfaceId: string) => void;
  readonly onSelectFairing?: (nodeId: string, transitionId: string) => void;
  readonly sectionDesigns?: readonly LoomProductSectionDesign[];
  readonly selectedNodeId?: string;
  readonly selection?: LoomProductSelection;
}

export interface LoomProductSectionDesign {
  readonly id: string;
  readonly label: string;
  readonly sourceNodeId: string;
  readonly state: "current" | "stale";
}

export type LoomProductSelection =
  | { readonly kind: "node"; readonly nodeId: string }
  | { readonly kind: "control"; readonly nodeId: string; readonly controlSurfaceId: string }
  | { readonly kind: "fairing"; readonly nodeId: string; readonly transitionId: string }
  | { readonly kind: "section"; readonly nodeId: string; readonly sectionId: string };

type PrimitiveNode = Extract<LoomSceneNode, { type: "primitive" }>;

function isPropulsion(node: PrimitiveNode): boolean {
  return node.definition.primitive === "pod" || node.definition.primitive === "revolution"
    || /propulsion|nacelle|engine|motor|rotor|propeller|actuator.?disk/i.test(node.label);
}

export function loomProductLabel(node: PrimitiveNode): string {
  return node.label;
}

function parentKey(key: string): string | null {
  const parts = key.split("/");
  return parts.length > 1 ? parts.slice(0, -1).join("/") : null;
}

export function LoomProductTree({
  aircraftName = "Aircraft", document, onOpenSection, onSelect, onSelectControl, onSelectFairing,
  sectionDesigns = [], selectedNodeId, selection,
}: LoomProductTreeProps) {
  const controls = useLoomControlSurfaceContext();
  const groups = useMemo(() => {
    const primitives = document.nodes.filter((node): node is PrimitiveNode => node.type === "primitive");
    const wings = primitives.filter((node) => node.definition.primitive === "wing");
    const propulsion = primitives.filter(isPropulsion);
    const bodies = primitives.filter((node) => node.definition.primitive !== "wing" && !isPropulsion(node));
    return { bodies, propulsion, wings };
  }, [document]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set([
    "aircraft", ...groups.wings.map((wing) => `aircraft/${wing.id}`),
  ]));
  const resolvedSelection: LoomProductSelection = selection ?? (controls?.selectedControl
    && controls.selectedWing ? { kind: "control", nodeId: controls.selectedWing.id,
      controlSurfaceId: controls.selectedControl.id }
    : { kind: "node", nodeId: selectedNodeId ?? "" });
  const activeSectionId = resolvedSelection.kind === "section" ? resolvedSelection.sectionId : null;
  const selectedControl = resolvedSelection.kind === "control" ? resolvedSelection : null;
  const selectedKey = resolvedSelection.kind === "section"
    ? `aircraft/${resolvedSelection.nodeId}/section:${resolvedSelection.sectionId}`
    : resolvedSelection.kind === "control"
      ? `aircraft/${resolvedSelection.nodeId}/control:${resolvedSelection.controlSurfaceId}`
      : resolvedSelection.kind === "fairing"
        ? `aircraft/${resolvedSelection.nodeId}/fairing:${resolvedSelection.transitionId}`
      : `aircraft/${resolvedSelection.nodeId}`;
  const [focusKey, setFocusKey] = useState(selectedKey);
  const focusRequested = useRef(false);
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const visibleKeys = useMemo(() => {
    const keys = ["aircraft"];
    if (!expanded.has("aircraft")) return keys;
    for (const wing of groups.wings) {
      const wingKey = `aircraft/${wing.id}`;
      keys.push(wingKey);
      if (!expanded.has(wingKey) || wing.definition.primitive !== "wing") continue;
      if (wing.definition.junctionFairing) {
        keys.push(`${wingKey}/fairing:${wing.definition.junctionFairing.transitionId}`);
      }
      sectionDesigns.filter((section) => section.sourceNodeId === wing.id)
        .forEach((section) => keys.push(`${wingKey}/section:${section.id}`));
      wing.definition.controlSurfaces?.controlSurfaces.forEach((control) => (
        keys.push(`${wingKey}/control:${control.id}`)
      ));
    }
    groups.bodies.forEach((node) => {
      const key = `aircraft/${node.id}`;
      keys.push(key);
      if (expanded.has(key)) sectionDesigns.filter((section) => section.sourceNodeId === node.id)
        .forEach((section) => keys.push(`${key}/section:${section.id}`));
    });
    groups.propulsion.forEach((node) => keys.push(`aircraft/${node.id}`));
    return keys;
  }, [expanded, groups, sectionDesigns]);

  useEffect(() => {
    if (visibleKeys.includes(selectedKey)) setFocusKey(selectedKey);
  }, [selectedKey, visibleKeys]);
  useEffect(() => {
    if (!visibleKeys.includes(focusKey)) setFocusKey(parentKey(focusKey) ?? "aircraft");
  }, [focusKey, visibleKeys]);
  useEffect(() => {
    if (!focusRequested.current) return;
    focusRequested.current = false;
    refs.current.get(focusKey)?.focus();
  }, [focusKey, visibleKeys]);

  const setOpen = (key: string, open: boolean) => setExpanded((current) => {
    const next = new Set(current);
    if (open) next.add(key); else next.delete(key);
    return next;
  });
  const moveFocus = (nextKey: string) => {
    focusRequested.current = true;
    setFocusKey(nextKey);
  };
  const selectControl = (nodeId: string, controlSurfaceId: string) => {
    if (onSelectControl) { onSelectControl(nodeId, controlSurfaceId); return; }
    onSelect(nodeId);
    controls?.selectControlSurface(controlSurfaceId);
  };
  const selectFairing = (nodeId: string, transitionId: string) => {
    if (onSelectFairing) { onSelectFairing(nodeId, transitionId); return; }
    onSelect(nodeId);
  };
  const keyDown = (key: string, expandable: boolean, event: KeyboardEvent<HTMLButtonElement>) => {
    const index = visibleKeys.indexOf(key);
    if (event.key === "ArrowDown" && index < visibleKeys.length - 1) moveFocus(visibleKeys[index + 1]!);
    else if (event.key === "ArrowUp" && index > 0) moveFocus(visibleKeys[index - 1]!);
    else if (event.key === "Home") moveFocus(visibleKeys[0]!);
    else if (event.key === "End") moveFocus(visibleKeys[visibleKeys.length - 1]!);
    else if (event.key === "ArrowRight" && expandable) {
      if (!expanded.has(key)) setOpen(key, true);
      else if (visibleKeys[index + 1]?.startsWith(`${key}/`)) moveFocus(visibleKeys[index + 1]!);
    } else if (event.key === "ArrowLeft") {
      if (expandable && expanded.has(key)) setOpen(key, false);
      else if (parentKey(key)) moveFocus(parentKey(key)!);
    } else return;
    event.preventDefault();
  };
  const itemProps = (key: string, level: number, expandable = false, selected = false) => ({
    "aria-expanded": expandable ? expanded.has(key) : undefined,
    "aria-level": level,
    "aria-selected": selected,
    onFocus: () => setFocusKey(key),
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => keyDown(key, expandable, event),
    ref: (node: HTMLButtonElement | null) => {
      if (node) refs.current.set(key, node); else refs.current.delete(key);
    },
    role: "treeitem" as const,
    tabIndex: focusKey === key ? 0 : -1,
  });

  return (
    <div className="loom-product-tree-wrap">
      <header><span>Aircraft</span><strong>Components</strong></header>
      <ul aria-label="Aircraft components" className="loom-product-tree" data-testid="loom-product-tree" role="tree">
        <li role="none">
          <button {...itemProps("aircraft", 1, true)} className="loom-product-root" onClick={() => setOpen("aircraft", !expanded.has("aircraft"))} type="button">
            <span aria-hidden="true">{expanded.has("aircraft") ? "⌄" : "›"}</span><b>{aircraftName}</b>
          </button>
          {expanded.has("aircraft") ? <ul role="group">
            {groups.wings.map((wing) => {
              const key = `aircraft/${wing.id}`;
              const junctionFairing = wing.definition.primitive === "wing"
                ? wing.definition.junctionFairing : undefined;
              const wingSelected = resolvedSelection.kind === "node" && resolvedSelection.nodeId === wing.id;
              return <li key={wing.id} role="none">
                <button {...itemProps(key, 2, true, wingSelected)} aria-current={wingSelected ? "true" : undefined} data-testid={`loom-product-node-${wing.id}`} onClick={() => { onSelect(wing.id); if (!onSelectControl) controls?.selectBody(`${wing.id}:fixed`); setOpen(key, true); }} type="button">
                  <span aria-hidden="true">{expanded.has(key) ? "⌄" : "›"}</span><span>{loomProductLabel(wing)}</span><em>Wing</em>
                </button>
                {expanded.has(key) && wing.definition.primitive === "wing" ? <ul role="group">
                  {junctionFairing ? <li role="none"><button
                    {...itemProps(`${key}/fairing:${junctionFairing.transitionId}`, 3,
                      false, resolvedSelection.kind === "fairing" && resolvedSelection.nodeId === wing.id
                        && resolvedSelection.transitionId === junctionFairing.transitionId)}
                    data-owner-id={wing.id} data-testid="loom-product-junction-fairing"
                    data-transition-id={junctionFairing.transitionId}
                    onClick={() => selectFairing(wing.id, junctionFairing.transitionId)}
                    type="button"
                  ><span>Wing–body fairing</span><em>Fairing</em></button></li> : null}
                  {sectionDesigns.filter((section) => section.sourceNodeId === wing.id).map((section) => {
                    const sectionKey = `${key}/section:${section.id}`;
                    return <li key={section.id} role="none"><button
                      {...itemProps(sectionKey, 3, false, activeSectionId === section.id)}
                      aria-selected={activeSectionId === section.id}
                      data-state={section.state}
                      data-testid="loom-product-section-design"
                      onClick={() => onOpenSection?.(wing.id, section.id)}
                      type="button"
                    ><span>{section.label}</span><em>{section.state === "stale" ? "Needs update" : "Session"}</em></button></li>;
                  })}
                  {wing.definition.controlSurfaces?.controlSurfaces.map((control) => {
                    const controlKey = `${key}/control:${control.id}`;
                    const controlSelected = activeSectionId === null && selectedControl?.nodeId === wing.id
                      && selectedControl.controlSurfaceId === control.id;
                    return <li key={control.id} role="none"><button
                      {...itemProps(controlKey, 3, false, controlSelected)}
                      aria-current={controlSelected ? "true" : undefined}
                      data-control-id={control.id} data-owner-id={wing.id}
                      data-testid={`loom-product-control-${wing.id}-${control.id}`}
                      onClick={() => selectControl(wing.id, control.id)}
                      type="button"
                    ><span>{control.label}</span><em>Control</em></button></li>;
                  })}
                </ul> : null}
              </li>;
            })}
            {groups.bodies.map((node) => {
              const key = `aircraft/${node.id}`;
              const sections = sectionDesigns.filter((section) => section.sourceNodeId === node.id);
              return <li key={node.id} role="none"><button
              {...itemProps(key, 2, sections.length > 0, resolvedSelection.kind === "node" && resolvedSelection.nodeId === node.id)}
              aria-current={resolvedSelection.kind === "node" && resolvedSelection.nodeId === node.id ? "true" : undefined}
              data-testid={`loom-product-node-${node.id}`}
              onClick={() => { onSelect(node.id); if (sections.length) setOpen(key, true); }}
              type="button"
            ><span>{loomProductLabel(node)}</span><em>Body</em></button>
              {sections.length && expanded.has(key) ? <ul role="group">{sections.map((section) => <li key={section.id} role="none"><button
                {...itemProps(`${key}/section:${section.id}`, 3, false, activeSectionId === section.id)}
                data-state={section.state} data-testid="loom-product-section-design"
                onClick={() => onOpenSection?.(node.id, section.id)} type="button"
              ><span>{section.label}</span><em>{section.state === "stale" ? "Needs update" : "Session"}</em></button></li>)}</ul> : null}
            </li>})}
            {groups.propulsion.map((node) => <li key={node.id} role="none"><button
              {...itemProps(`aircraft/${node.id}`, 2, false, resolvedSelection.kind === "node" && resolvedSelection.nodeId === node.id)}
              aria-current={resolvedSelection.kind === "node" && resolvedSelection.nodeId === node.id ? "true" : undefined}
              data-testid={`loom-product-node-${node.id}`}
              onClick={() => onSelect(node.id)}
              type="button"
            ><span>{loomProductLabel(node)}</span><em>Propulsion</em></button></li>)}
          </ul> : null}
        </li>
      </ul>
    </div>
  );
}
